import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ArrayControlProps,
  composePaths,
  ControlElement,
  createDefaultValue,
  findUISchema,
  Helpers,
  isObjectArrayWithNesting,
  RankedTester,
  rankWith,
} from "@jsonforms/core";
import {
  JsonFormsDispatch,
  withJsonFormsArrayControlProps,
} from "@jsonforms/react";
import range from "lodash/range";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";
import { useMemo } from "react";

export const ArrayControl = (props: ArrayControlProps) => {
  const {
    data,
    label,
    path,
    schema,
    errors,
    addItem,
    removeItems,
    moveUp,
    moveDown,
    uischema,
    uischemas,
    renderers,
    rootSchema,
    enabled,
  } = props;

  const childUiSchema = useMemo(
    () =>
      findUISchema(
        uischemas!,
        schema,
        uischema.scope,
        path,
        undefined,
        uischema,
        rootSchema,
      ),
    [uischemas, schema, uischema.scope, path, uischema, rootSchema],
  );

  return (
    <div className="space-y-2 mb-4">
      <header className="flex items-center justify-between">
        <Label>{label}</Label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                disabled={!enabled}
                onClick={addItem?.(
                  path,
                  createDefaultValue(schema, rootSchema),
                )}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Add to {label}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </header>

      <div>{errors}</div>

      <div className="space-y-2">
        {data ? (
          <Accordion type="single" collapsible>
            {range(0, data.length).map((index) => {
              const childPath = composePaths(path, `${index}`);
              return (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="group">
                    <div className="flex w-full items-center justify-between mr-2">
                      <span>{`${label} ${index + 1}`}</span>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={!enabled || index === 0}
                          onClick={(e) => {
                            e.stopPropagation();
                            moveUp?.(path, index)();
                          }}
                        >
                          <ChevronUp />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={!enabled || index === data.length - 1}
                          onClick={(e) => {
                            e.stopPropagation();
                            moveDown?.(path, index)();
                          }}
                        >
                          <ChevronDown />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={!enabled}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              window.confirm(
                                "Are you sure you wish to delete this item?",
                              )
                            ) {
                              removeItems?.(path, [index])();
                            }
                          }}
                        >
                          <Trash2 />
                        </Button>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <JsonFormsDispatch
                      schema={schema}
                      uischema={childUiSchema || uischema}
                      path={childPath}
                      key={childPath}
                      renderers={renderers}
                    />
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        ) : (
          <p className="text-sm text-muted-foreground">
            No {label.toLowerCase()} yet
          </p>
        )}
      </div>
    </div>
  );
};

export const ArrayControlRenderer = (props: ArrayControlProps) => {
  const {
    schema,
    uischema,
    data,
    path,
    rootSchema,
    uischemas,
    addItem,
    removeItems,
    moveUp,
    moveDown,
    id,
    visible,
    enabled,
    errors,
  } = props;

  const controlElement = uischema as ControlElement;
  const labelDescription = Helpers.createLabelDescriptionFrom(
    controlElement,
    schema,
  );
  const label = labelDescription.show ? labelDescription.text : "";

  const childUiSchema = useMemo(
    () =>
      findUISchema(
        uischemas!,
        schema,
        uischema.scope,
        path,
        undefined,
        uischema,
        rootSchema,
      ),
    [uischemas, schema, uischema.scope, path, uischema, rootSchema],
  );

  return (
    <ArrayControl
      data={data}
      label={label!}
      path={path}
      schema={schema}
      errors={errors}
      addItem={addItem}
      removeItems={removeItems}
      moveUp={moveUp}
      moveDown={moveDown}
      uischema={uischema}
      uischemas={uischemas}
      rootSchema={rootSchema}
      id={id}
      visible={visible}
      enabled={enabled}
      arraySchema={childUiSchema}
    />
  );
};

export const arrayControlTester: RankedTester = rankWith(
  5,
  isObjectArrayWithNesting,
);

export default withJsonFormsArrayControlProps(ArrayControlRenderer);
